import { useState, useEffect, useMemo, useRef } from 'react';
//eslint-disable-next-line
import { geohashQueryBounds, Geopoint } from 'geofire-common';

import { Marker } from '@/types';
import { CompassContextType } from '@/src/libs/CompassContext';

import { createMarkersFromFetchedData } from '../utils/map-utils/createMarkersFromFetchedData';
import { fetchMeetingsInRange } from '../utils/map-utils/fetchMeetingsInRange';
import { shouldShowMarkerAndMeetings } from '../helpers/shouldShowMarkerAndMeetings';

export const useFetchMeetings = (
  ctx: CompassContextType,
  initialLocationSet: boolean
): {
  data: Marker[];
  error: Error | null;
  isLoading: boolean;
} => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [data, setData] = useState<Marker[]>([]);
  const [unfilteredData, setUnfilteredData] = useState<Marker[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const pendingCenters = useRef(new Set<string>());

  useMemo(() => {
    const filteredData = shouldShowMarkerAndMeetings(unfilteredData, ctx);
    if (filteredData.length && !ctx.state.meetingFocus) {
      let counter = 0;
      while (counter < filteredData.length) {
        if (filteredData[counter].marker.map) {
          ctx.dispatch({
            type: 'SET_MEETING_FOCUS',
            payload: filteredData[counter].markerId,
          });
          break;
        }
        counter++;
      }
    }
    setData(filteredData);
  }, [unfilteredData, ctx.state.filters]);

  useEffect(() => {
    if (!initialLocationSet) return;

    ctx.dispatch({
      type: 'SHOW_FETCH_BUTTON',
      payload: false,
    });

    const fetchMeetingsFromMapCenter = async (
      center: google.maps.LatLngLiteral
    ) => {
      const centerKey = `${center.lat},${center.lng}`;
      if (pendingCenters.current.has(centerKey)) return;

      pendingCenters.current.add(centerKey);
      setIsLoading(true);
      setError(null);

      ctx.dispatch({
        type: 'SET_MEETING_FOCUS',
        payload: '',
      });

      if (data.length) {
        for (const marker of data) {
          marker.marker.map = null;
        }
      }

      try {
        const hashRange = getGeohashRanges(center);

        const meetings = await fetchMeetingsInRange(
          ctx.state.firestore,
          hashRange
        );

        if (!meetings.length) {
          setUnfilteredData([]);
        } else {
          const markersData = await createMarkersFromFetchedData(meetings, ctx);

          setUnfilteredData(markersData);
        }

        setCenter(center);
      } catch (error) {
        setUnfilteredData([]);
        setError(error as Error);
      } finally {
        pendingCenters.current.delete(centerKey);
        setIsLoading(false);
      }
    };

    const dependencyErrors: string[] = [];

    if (!ctx) dependencyErrors.push('No context found');

    if (!ctx.state.firestore) {
      dependencyErrors.push('No Firestore instance found');
    }

    const mapCenter = ctx.state.mapCenter;

    if (!mapCenter) dependencyErrors.push('No map center provided');

    if (dependencyErrors.length) {
      setError(new Error(dependencyErrors.join('\n')));
    } else {
      if (
        mapCenter.lat !== center?.lat ||
        mapCenter.lng !== center?.lng ||
        !center
      ) {
        fetchMeetingsFromMapCenter(mapCenter);
      }
    }
  }, [
    ctx.state.firestore,
    ctx.state.map,
    ctx.state.mapCenter,
    initialLocationSet,
  ]);
  return { data, error, isLoading };
};

const getGeohashRanges = (
  center: google.maps.LatLngLiteral
): [string, string][] => {
  const geopointCenter: Geopoint = [center.lat, center.lng];
  return geohashQueryBounds(geopointCenter, 5000);
};
